import { type MouseEvent, type PropsWithChildren, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MdCopyAll } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useCopyToClipboard } from 'react-use';

import { IconButton, type IconButtonProps, useTheme } from '@mui/material';

import { useAppSelector } from '@hooks';

interface ICopyButtonProperties {
  value: string;
  fieldName: string;
  withIcon?: boolean;
  sx?: Exclude<IconButtonProps['sx'], undefined>;
}

export const CopyButton = ({
  value,
  sx = {},
  children,
  fieldName,
  withIcon = true,
}: PropsWithChildren<ICopyButtonProperties>) => {
  const [, copyTextToClipboard] = useCopyToClipboard();
  const theme = useTheme();
  const { t } = useTranslation();

  const isPlatformTheme = useAppSelector(
    (state) => state.appConfig.config.isPlatformTheme
  );

  const handleCopy = useCallback(
    (
      event: MouseEvent<HTMLButtonElement>,
      { value, fieldName }: ICopyButtonProperties
    ) => {
      event.stopPropagation();
      copyTextToClipboard(value ?? '');
      toast.success(t('common.toast.copied', { fieldName }));
    },
    [copyTextToClipboard, t]
  );

  return (
    <IconButton
      onClick={(event) => {
        handleCopy(event, {
          value,
          fieldName,
        });
      }}
      sx={{
        padding: '0',
        width: '1.375rem',
        height: '1.375rem',
        color: isPlatformTheme
          ? theme.palette.primary.darker
          : theme.palette.grey[500],
        ...sx,
      }}
    >
      {children}
      {withIcon ? <MdCopyAll /> : null}
    </IconButton>
  );
};
